// Bulk "Assign B1" modal — sets the B1 (Loan Administrator) pill on every
// selected loan via the existing per-loan assignment endpoint. Staff are
// picked by name (UserPicker typeahead) — never by raw user id.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { UserPicker } from "@/components/users/UserPicker";
import { loanAssignmentsApi } from "@/api/portfolioAdmin/client";
import { portfolioAdminKeys } from "@/hooks/portfolioAdmin/usePortfolioAdmin";

interface AssignB1ModalProps {
  loanIds: string[];
  onClose: () => void;
  onDone: (assignedCount: number) => void;
}

export function AssignB1Modal({ loanIds, onClose, onDone }: AssignB1ModalProps) {
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  async function submit() {
    if (!userId) return;
    setSaving(true);
    setError(null);
    try {
      for (const loanId of loanIds) {
        await loanAssignmentsApi.upsert(loanId, "B1", {
          action: "assign",
          assignedUserId: userId,
        });
        void qc.invalidateQueries({
          queryKey: portfolioAdminKeys.assignments(loanId),
        });
      }
      void qc.invalidateQueries({ queryKey: ["portfolio-admin", "loans"] });
      onDone(loanIds.length);
    } catch (e) {
      setError((e as Error).message ?? "Assignment failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Assign B1 — ${loanIds.length} ${loanIds.length === 1 ? "loan" : "loans"}`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={saving}
            disabled={!userId || saving}
            onClick={() => void submit()}
            data-testid="assign-b1-submit"
          >
            Assign
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-2" data-testid="assign-b1-modal">
        <span className="text-caption text-gray-mid uppercase tracking-label">
          Loan Administrator (B1)
        </span>
        <UserPicker
          value={userId}
          initialDisplayName={userName}
          autoFocus
          ariaLabel="Assign B1 to"
          placeholder="Search staff by name…"
          onChange={(id, displayName) => {
            setUserId(id);
            setUserName(displayName);
          }}
        />
        {error && (
          <p className="text-label text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
